import { Button, DrawerCard, DrawerField, Input, Select, Tooltip } from "@mixitone/components";
import LinkIcon from "@mixitone/icons/link.svg";
import LockIcon from "@mixitone/icons/lock.svg";
import StartIcon from "@mixitone/icons/start.svg";
import CheckIcon from "@mixitone/icons/tick.svg";
import TrophyIcon from "@mixitone/icons/trophy.svg";
import UnlockIcon from "@mixitone/icons/unlock.svg";
import WarnIcon from "@mixitone/icons/warn.svg";
import type { TournamentMatch } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import EntryNames from "components/Pages/Tournament/EntryNames";
import { TournamentMatchFormController } from "forms/TournamentMatchFormController";
import { useRef } from "react";
import { Fragment } from "react/jsx-runtime";
import routeService from "services/RouteService";
import { matchVoidDisplayLabel } from "tournament-state/TournamentEventTypes";
import { TournamentEntrySearch } from "./TournamentEntrySearch";

const TournamentMatchForm = () => {
  const lockButtonRef = useRef<HTMLButtonElement>(null);
  const controller = TournamentMatchFormController.use();
  const {
    entries,
    allCompetitionEntries,
    winner,
    status,
    record,
    saving,
    division,
    selectedCompetition,
    numCourts,
  } = controller.state;
  const openConflictsForMatch = controller.openConflictsForCurrentMatch;

  if (!record) return null;

  const isCustomFormat = controller.isCustomFormat;
  // Use selectedCompetition from state (which is reloaded) or fall back to record.competition
  const competition = selectedCompetition || record.competition;
  const hasDivisions = competition?.hasDivisions() ?? false;
  const splitRound = competition?.getDivisionSplitRound();
  const numDivisions = competition?.getNumDivisions();
  const showDivisionSelector = hasDivisions && splitRound !== undefined && (record.round ?? 0) >= splitRound;
  const effectiveStatus = status || record.status || "pending";
  const isPending = effectiveStatus === "pending";
  const isReady = effectiveStatus === "ready";
  const isInProgress = effectiveStatus === "in_progress";
  const isComplete = effectiveStatus === "completed";
  const isVoided = effectiveStatus === "voided";
  const voidLabel = matchVoidDisplayLabel(
    typeof record.format_data?.void_reason === "string" ? record.format_data.void_reason : undefined,
  );

  const entryLabel = (entryId?: string) => {
    if (!entryId) return "Unknown entry";
    const entry = allCompetitionEntries.findById(entryId) || entries.findById(entryId);
    return entry ? entry.name : entryId;
  };

  const resolveWinnerIds = Array.from(
    new Set(
      openConflictsForMatch.flatMap((conflict) => {
        const existingWinnerId =
          typeof conflict.payload?.existing_winner_id === "string"
            ? conflict.payload.existing_winner_id
            : undefined;
        const nextWinnerId =
          typeof conflict.payload?.next_winner_id === "string" ? conflict.payload.next_winner_id : undefined;
        return [existingWinnerId, nextWinnerId].filter((id): id is string => !!id);
      }),
    ),
  );

  return (
    <div className="space-y-6">
      {openConflictsForMatch.length > 0 && (
        <DrawerCard>
          <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
            <div className="flex items-start gap-2 text-amber-900">
              <WarnIcon className="mt-0.5 h-5 w-5 text-amber-700" />
              <div>
                <p className="font-semibold">
                  {openConflictsForMatch.length > 1
                    ? `${openConflictsForMatch.length} winner conflicts detected for this match`
                    : "Winner conflict detected for this match"}
                </p>
                <p className="text-sm">
                  Choose the authoritative winner to resolve these conflicts and regenerate the bracket.
                </p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              {openConflictsForMatch.map((conflict, index) => {
                const existingWinnerId =
                  typeof conflict.payload?.existing_winner_id === "string"
                    ? conflict.payload.existing_winner_id
                    : undefined;
                const nextWinnerId =
                  typeof conflict.payload?.next_winner_id === "string"
                    ? conflict.payload.next_winner_id
                    : undefined;

                return (
                  <div
                    key={conflict.conflict_key || conflict.id || `conflict-${index}`}
                    className="rounded border border-amber-200 bg-white p-2"
                  >
                    <div className="font-medium text-amber-900">
                      {entryLabel(existingWinnerId)} vs {entryLabel(nextWinnerId)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              {resolveWinnerIds.map((winnerId) => (
                <Button
                  key={winnerId}
                  kind="accent"
                  onClick={() => controller.actionResolveConflict(winnerId)}
                  disabled={saving}
                >
                  Resolve with {entryLabel(winnerId)}
                </Button>
              ))}
            </div>
          </div>
        </DrawerCard>
      )}
      {/* Match Info */}
      <DrawerCard>
        <DrawerField label="Competition">{record.competition?.name}</DrawerField>
        <DrawerField label="Round">{record.round! + 1}</DrawerField>
        {showDivisionSelector && (
          <DrawerField label="Division">
            <Select
              value={division?.toString() || ""}
              onChange={(e) => {
                const value = e.target.value;
                controller.actionUpdateDivision(value ? Number.parseInt(value, 10) : undefined);
              }}
              disabled={saving}
            >
              <option value="">Select division</option>
              {numDivisions &&
                Array.from({ length: numDivisions }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n.toString()}>
                    Division {n}
                  </option>
                ))}
            </Select>
          </DrawerField>
        )}
        <DrawerField label="Status">
          {isVoided ? (
            <p className="text-sm font-medium text-slate-700">{voidLabel}</p>
          ) : (
            <Select
              value={status}
              onChange={(e) => {
                controller.actionUpdateStatus(e.target.value as TournamentMatch["status"]);
              }}
              disabled={saving}
            >
              <option value="pending">Pending</option>
              <option value="ready">Ready</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </Select>
          )}
        </DrawerField>
        {!isVoided && (isReady || isPending || isInProgress) && numCourts > 0 && (
          <DrawerField label="Assign to court">
            <div className="flex items-center gap-2">
              <Select
                value={record.court_id || ""}
                onChange={(e) => {
                  controller.actionUpdateCourt(e.target.value || undefined);
                }}
                disabled={saving || isInProgress}
                className="flex-1"
              >
                <option value="">No court assigned</option>
                {Array.from({ length: numCourts }, (_, i) => i + 1).map((courtNum) => (
                  <option key={courtNum} value={courtNum.toString()}>
                    Court {courtNum}
                  </option>
                ))}
              </Select>
              {!record.court_id && (isReady || isPending) && (
                <Button kind="secondary" onClick={() => controller.actionAssignCourt()} disabled={saving}>
                  Auto assign
                </Button>
              )}
              {record.court_id && isReady && (
                <Button kind="accent" title="Start" onClick={() => controller.actionStartMatch()}>
                  <StartIcon className="h-4" />
                </Button>
              )}
              {isInProgress && (
                <Button kind="accent" title="Finish" onClick={() => controller.actionFinishMatch()}>
                  <CheckIcon className="h-4 stroke-white" />
                </Button>
              )}
            </div>
          </DrawerField>
        )}
      </DrawerCard>

      <DrawerCard>
        <div className="space-y-4">
          <div className="grid w-full grid-cols-[1fr_min-content_min-content] gap-3">
            {isVoided && (
              <>
                <div className="text-sm font-semibold text-slate-700">{voidLabel}</div>
                <div />
                <div />
              </>
            )}
            {isComplete && !isVoided && (
              <>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <TrophyIcon className="inline w-4 h-4 fill-yellow-500" />
                  <span>Select winner</span>
                </div>
                <div className="text-sm text-gray-600">Score</div>
                <div />
              </>
            )}
            {!isComplete && !isVoided && (
              <>
                <div className="text-sm text-gray-600">Entries</div>
                <div />
                <div>
                  {!isInProgress && (
                    <>
                      <Button
                        ref={lockButtonRef}
                        kind="plain"
                        onClick={(event) => {
                          event.preventDefault();
                          controller.actionUnlockEntries();
                        }}
                        disabled={saving}
                      >
                        {record.matchEntriesLocked && <LockIcon className="w-4 h-4" />}
                        {!record.matchEntriesLocked && <UnlockIcon className="w-4 h-4" />}
                      </Button>
                      {record.matchEntriesLocked && (
                        <Tooltip hover targetRef={lockButtonRef}>
                          <p>These entries have been manually assigned.</p>
                          <p> Click to unlock, to re-assign matches based on seed.</p>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {!isComplete &&
              !isVoided &&
              (isCustomFormat ? (
                <>
                  {/* Entry 1 */}
                  <div className="relative z-10 col-span-2">
                    {entries[0] ? (
                      <div
                        className="flex items-center justify-between p-3 transition-colors border border-gray-200 rounded-lg bg-gray-50"
                        onClick={() => {
                          if (controller.state.status === "completed" && entries[0]?.id) {
                            controller.actionUpdateWinner(entries[0].id!);
                          }
                        }}
                      >
                        <EntryNames entry={entries[0]} className="font-medium" />
                        {!isInProgress && (
                          <Button
                            kind="plain"
                            className="text-red-500"
                            onClick={() => controller.actionRemoveEntry(0)}
                            disabled={saving}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ) : (
                      <TournamentEntrySearch
                        entries={allCompetitionEntries}
                        value={null}
                        onSelect={(entry) => controller.actionAssignEntry(entry, 0)}
                        placeholder="Select first entry"
                        disabled={saving}
                      />
                    )}
                  </div>
                  <div />
                  {/* Entry 2 */}
                  <div className="relative z-10 col-span-2">
                    {entries[1] ? (
                      <div
                        className="flex items-center justify-between p-3 transition-colors border border-gray-200 rounded-lg bg-gray-50"
                        onClick={() => {
                          if (controller.state.status === "completed" && entries[1]?.id) {
                            controller.actionUpdateWinner(entries[1].id!);
                          }
                        }}
                      >
                        <EntryNames entry={entries[1]} className="font-medium" />
                        {!isInProgress && (
                          <Button
                            kind="plain"
                            className="text-red-500"
                            onClick={() => controller.actionRemoveEntry(1)}
                            disabled={saving}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ) : (
                      <TournamentEntrySearch
                        entries={allCompetitionEntries}
                        value={null}
                        onSelect={(entry) => controller.actionAssignEntry(entry, 1)}
                        placeholder="Select second entry (optional)"
                        disabled={saving}
                      />
                    )}
                  </div>
                  <div />
                </>
              ) : (
                entries.map((entry) => (
                  <Fragment key={entry.id}>
                    <div className="flex items-center justify-between col-span-2 p-3 transition-colors border border-gray-200 rounded-lg bg-gray-50">
                      <EntryNames entry={entry} className="font-medium" />
                    </div>
                    <Button
                      kind="plain"
                      className="text-blue-500"
                      onClick={() => {
                        // Close myself
                        controller.actionCloseModal();
                        routeService.openModal("/tournament/:id/entries/:entryId/edit", {
                          params: {
                            id: record.competition.tournament_session_id!,
                            entryId: entry.id!,
                          },
                        });
                      }}
                    >
                      <LinkIcon className="w-4 h-4" />
                    </Button>
                  </Fragment>
                ))
              ))}

            {isVoided &&
              entries
                .filter((entry): entry is NonNullable<(typeof entries)[number]> => !!entry)
                .map((entry) => (
                  <Fragment key={entry.id}>
                    <div className="col-span-2 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <EntryNames entry={entry} className="font-medium" />
                    </div>
                    <div />
                  </Fragment>
                ))}

            {isComplete &&
              !isVoided &&
              entries
                .filter((entry): entry is NonNullable<(typeof entries)[number]> => !!entry)
                .map((entry, idx) => (
                  <Fragment key={entry.id}>
                    <button
                      type="button"
                      data-testid={`tournament-match-winner-${entry.id}`}
                      tabIndex={idx + 8}
                      onClick={() => {
                        controller.actionUpdateWinner(entry.id!);
                      }}
                      className={clsx(
                        Input.style,
                        "m-0 flex w-full items-center justify-between transition-colors cursor-pointer",
                        {
                          "border-green-700 bg-green-50 text-green-700 hover:bg-green-100":
                            winner === entry.id,
                          "border-gray-200 hover:bg-gray-50": winner !== entry.id,
                        },
                      )}
                    >
                      {winner !== entry.id && <span className="text-gray-400 text-[10px]">◯</span>}
                      {winner === entry.id && <span className="text-green-600">◉</span>}
                      <EntryNames entry={entry} className="font-medium" />
                    </button>
                    <Input
                      tabIndex={idx + 9}
                      type="text"
                      className="w-16"
                      value={controller.state.score[entry.id!] || ""}
                      onChange={(event) => {
                        controller.actionUpdateScore(entry.id!, event.target.value);
                      }}
                      onBlur={() => controller.actionSubmit()}
                    />
                    <Button
                      kind="plain"
                      className="text-blue-500"
                      onClick={() => {
                        // Close myself
                        controller.actionCloseModal();
                        routeService.openModal("/tournament/:id/entries/:entryId/edit", {
                          params: {
                            id: record.competition.tournament_session_id!,
                            entryId: entry.id!,
                          },
                        });
                      }}
                    >
                      <LinkIcon className="w-4 h-4" />
                    </Button>
                  </Fragment>
                ))}
          </div>
        </div>
      </DrawerCard>
      {controller.canDelete && (
        <DrawerCard>
          <div className="self-end">
            <Button kind="danger" onClick={() => controller.actionDelete()} disabled={saving}>
              Delete match
            </Button>
          </div>
        </DrawerCard>
      )}
      {controller.hasMatchList && (
        <DrawerCard>
          <div className="self-end">
            {controller.canVisitNext && (
              <Button onClick={() => controller.actionNext()} disabled={saving}>
                Next match
              </Button>
            )}
            {!controller.canVisitNext && (
              <Button onClick={() => controller.actionCloseModal()} disabled={saving}>
                Done
              </Button>
            )}
          </div>
        </DrawerCard>
      )}
    </div>
  );
};

// Use scope to wrap the form with the controller
export default TournamentMatchFormController.scope(ApplicationView(TournamentMatchForm));
